/* eslint-disable no-console */
import api from '../../config/axios.js';
import Pusher from 'pusher-js';

const emptyMoney = { raw: 0, formatted: 'R$ 0,00' };
let financePusher = null;
let financeChannel = null;

const state = {
    valuesVisible: true,
    valuesVisibilityUserId: null,
    dashboard: {
        current_month: emptyMoney,
        previous_month: emptyMoney,
        month_comparison: {
            difference: emptyMoney,
            percentage: null,
        },
        last_15_days: emptyMoney,
        current_week: emptyMoney,
        recurring_expenses: {
            total: emptyMoney,
            items: [],
        },
        categories: [],
    },
    transactions: [],
    transactionsPagination: {
        current_page: 1,
        last_page: 1,
        per_page: 10,
        total: 0,
        from: null,
        to: null,
    },
    categories: [],
    recurringExpenses: [],
    recurringExpensesPagination: {
        current_page: 1,
        last_page: 1,
        per_page: 10,
        total: 0,
        from: null,
        to: null,
    },
    recurringExpensesSummary: {
        monthly_estimate: emptyMoney,
        active_count: 0,
        overdue_count: 0,
        upcoming_count: 0,
        unestimated_count: 0,
    },
    loadingFinance: false,
    financeError: '',
    realtimeConnected: false,
    realtimeLastEvent: null,
    realtimeChannelName: '',
    lastDashboardParams: {},
    lastTransactionParams: {},
    lastRecurringParams: {},
};

const getters = {
    getFinanceValuesVisible: state => state.valuesVisible,
    getFinanceDashboard: state => state.dashboard,
    getFinanceTransactions: state => state.transactions,
    getFinanceTransactionsPagination: state => state.transactionsPagination,
    getFinanceCategories: state => state.categories,
    getFinanceRecurringExpenses: state => state.recurringExpenses,
    getFinanceRecurringExpensesPagination: state => state.recurringExpensesPagination,
    getFinanceRecurringExpensesSummary: state => state.recurringExpensesSummary,
    getLoadingFinance: state => state.loadingFinance,
    getFinanceError: state => state.financeError,
    getFinanceRealtimeConnected: state => state.realtimeConnected,
    getFinanceRealtimeLastEvent: state => state.realtimeLastEvent,

    // Compatibility aliases used by older components while the app is being remodeled.
    getFinancialData: state => state.transactions,
    getFinancialPagination: state => state.transactionsPagination,
    getExpenseData: state => state.transactions,
    getCardsFinancialManagementData: state => ({
        amount_received: state.dashboard.current_month?.formatted || 'R$ 0,00',
        amount_expense: state.dashboard.current_month?.formatted || 'R$ 0,00',
        qtd_expense: state.transactionsPagination.total || state.transactions.length,
        qtd_schedules: 0,
    }),
};

const actions = {
    initializeFinanceValuesVisibility({ commit, state }, userId = 'default') {
        const normalizedUserId = String(userId || 'default');
        if (state.valuesVisibilityUserId === normalizedUserId) return;

        let valuesVisible = true;
        if (typeof window !== 'undefined') {
            try {
                const storageKey = `fin.values-visibility.${normalizedUserId}`;
                const legacyStorageKey = `fin.dashboard.values-visibility.${normalizedUserId}`;
                const storedPreference = window.localStorage.getItem(storageKey)
                    ?? window.localStorage.getItem(legacyStorageKey);
                valuesVisible = storedPreference !== 'hidden';
            } catch (error) {
            if (api.axios.isCancel(error)) return;
                valuesVisible = true;
            }
        }

        commit('setFinanceValuesVisibility', {
            userId: normalizedUserId,
            visible: valuesVisible,
        });
    },

    toggleFinanceValuesVisibility({ commit, state }) {
        const userId = state.valuesVisibilityUserId || 'default';
        const visible = !state.valuesVisible;

        commit('setFinanceValuesVisibility', { userId, visible });

        if (typeof window !== 'undefined') {
            try {
                window.localStorage.setItem(
                    `fin.values-visibility.${userId}`,
                    visible ? 'visible' : 'hidden'
                );
            } catch (error) {
            if (api.axios.isCancel(error)) return;
                // The reactive preference still works when storage is unavailable.
            }
        }
    },

    async fetchFinanceDashboard({ commit }, params = {}) {
        commit('setLoadingFinance', true);
        commit('setFinanceError', '');
        commit('setLastDashboardParams', params);

        try {
            const response = await api.axios.get('/api/finance/dashboard', { params });
            commit('setFinanceDashboard', response.data || {});
        } catch (error) {
            if (api.axios.isCancel(error)) return;
            commit('setFinanceError', 'Erro ao carregar dashboard financeiro.');
        } finally {
            commit('setLoadingFinance', false);
        }
    },

    async fetchFinanceTransactions({ commit }, params = {}) {
        commit('setLoadingFinance', true);
        commit('setFinanceError', '');
        commit('setLastTransactionParams', params);

        try {
            const response = await api.axios.get('/api/finance/transactions', { params });
            const payload = response.data || {};
            const meta = payload.meta || {};
            const list = Array.isArray(payload.data) ? payload.data : (Array.isArray(payload) ? payload : []);

            commit('setFinanceTransactions', list);
            commit('setFinanceTransactionsPagination', {
                current_page: Number(payload.current_page || meta.current_page || params.page || 1),
                last_page: Number(payload.last_page || meta.last_page || 1),
                per_page: Number(payload.per_page || meta.per_page || params.per_page || list.length || 10),
                total: Number(payload.total || meta.total || list.length),
                from: payload.from || meta.from || null,
                to: payload.to || meta.to || null,
            });
        } catch (error) {
            if (api.axios.isCancel(error)) return;
            commit('setFinanceError', 'Erro ao carregar lançamentos.');
            commit('setFinanceTransactions', []);
        } finally {
            commit('setLoadingFinance', false);
        }
    },

    async fetchFinanceCategoryTransactions(_context, params = {}) {
        const response = await api.axios.get('/api/finance/transactions', { params });
        return response.data || {};
    },

    async createFinanceTransaction({ dispatch }, payload) {
        await api.axios.post('/api/finance/transactions', payload);
        await Promise.all([
            dispatch('fetchFinanceTransactions', state.lastTransactionParams),
            dispatch('fetchFinanceDashboard', state.lastDashboardParams),
        ]);
    },

    async updateFinanceTransaction({ dispatch }, { id, data }) {
        const response = await api.axios.patch(`/api/finance/transactions/${id}`, data);
        await Promise.all([
            dispatch('fetchFinanceTransactions', state.lastTransactionParams),
            dispatch('fetchFinanceDashboard', state.lastDashboardParams),
        ]);

        return response.data;
    },

    async deleteFinanceTransaction({ dispatch }, id) {
        await api.axios.delete(`/api/finance/transactions/${id}`);
        await Promise.all([
            dispatch('fetchFinanceTransactions', state.lastTransactionParams),
            dispatch('fetchFinanceDashboard', state.lastDashboardParams),
        ]);
    },

    async fetchFinanceCategories({ commit }) {
        try {
            const response = await api.axios.get('/api/finance/categories');
            commit('setFinanceCategories', response.data || []);
        } catch (error) {
            if (api.axios.isCancel(error)) return;
            commit('setFinanceCategories', []);
        }
    },

    async fetchFinanceRecurringExpenses({ commit }, params = {}) {
        commit('setLoadingFinance', true);
        commit('setFinanceError', '');
        commit('setLastRecurringParams', params);

        try {
            const response = await api.axios.get('/api/finance/recurring-expenses', { params });
            const payload = response.data || {};
            const meta = payload.meta || {};
            const list = Array.isArray(payload.data) ? payload.data : (Array.isArray(payload) ? payload : []);

            commit('setFinanceRecurringExpenses', list);
            commit('setFinanceRecurringExpensesSummary', payload.summary || {});
            commit('setFinanceRecurringExpensesPagination', {
                current_page: Number(payload.current_page || meta.current_page || params.page || 1),
                last_page: Number(payload.last_page || meta.last_page || 1),
                per_page: Number(payload.per_page || meta.per_page || params.per_page || list.length || 10),
                total: Number(payload.total || meta.total || list.length),
                from: payload.from || meta.from || null,
                to: payload.to || meta.to || null,
            });
        } catch (error) {
            if (api.axios.isCancel(error)) return;
            commit('setFinanceError', 'Erro ao carregar compromissos fixos.');
            commit('setFinanceRecurringExpenses', []);
        } finally {
            commit('setLoadingFinance', false);
        }
    },

    async fetchFinanceRecurringExpensesList(_context, params = {}) {
        const items = [];
        let page = 1;
        let lastPage = 1;

        do {
            const response = await api.axios.get('/api/finance/recurring-expenses', {
                params: { ...params, page, per_page: 100 },
            });
            const payload = response.data || {};
            const meta = payload.meta || {};
            const list = Array.isArray(payload.data) ? payload.data : (Array.isArray(payload) ? payload : []);

            items.push(...list);
            lastPage = Number(payload.last_page || meta.last_page || 1);
            page += 1;
        } while (page <= lastPage);

        return items;
    },

    async createFinanceRecurringExpense({ dispatch }, payload) {
        await api.axios.post('/api/finance/recurring-expenses', payload);
        await Promise.all([
            dispatch('fetchFinanceRecurringExpenses', state.lastRecurringParams),
            dispatch('fetchFinanceDashboard', state.lastDashboardParams),
        ]);
    },

    async updateFinanceRecurringExpense({ dispatch }, { id, data }) {
        await api.axios.patch(`/api/finance/recurring-expenses/${id}`, data);
        await Promise.all([
            dispatch('fetchFinanceRecurringExpenses', state.lastRecurringParams),
            dispatch('fetchFinanceDashboard', state.lastDashboardParams),
        ]);
    },

    async deleteFinanceRecurringExpense({ dispatch }, id) {
        await api.axios.delete(`/api/finance/recurring-expenses/${id}`);
        await Promise.all([
            dispatch('fetchFinanceRecurringExpenses', state.lastRecurringParams),
            dispatch('fetchFinanceDashboard', state.lastDashboardParams),
        ]);
    },

    startFinanceRealtime({ commit, dispatch, rootGetters }) {
        const companyId = rootGetters.getCompany_id;
        const pusherKey = process.env.VUE_APP_PUSHER_KEY;
        const pusherCluster = process.env.VUE_APP_PUSHER_CLUSTER || 'us2';

        console.log('[PUSHER FINANCEIRO] Inicializando realtime financeiro', {
            company_id: companyId,
            has_pusher_key: Boolean(pusherKey),
            cluster: pusherCluster,
        });

        if (!companyId || !pusherKey) {
            console.warn('[PUSHER FINANCEIRO] Nao foi possivel iniciar realtime: company_id ou VUE_APP_PUSHER_KEY ausente', {
                company_id: companyId,
                has_pusher_key: Boolean(pusherKey),
            });
            commit('setFinanceRealtimeConnected', false);
            return;
        }

        const channelName = `private-finance.${companyId}`;
        if (financeChannel && state.realtimeChannelName === channelName) {
            return;
        }

        if (financePusher) {
            financePusher.disconnect();
            financePusher = null;
            financeChannel = null;
        }

        financePusher = new Pusher(pusherKey, {
            cluster: pusherCluster,
            forceTLS: true,
            authorizer: (channel) => ({
                authorize: (socketId, callback) => {
                    console.log('[PUSHER FINANCEIRO] Autorizando canal privado', {
                        channel: channel.name,
                        socket_id: socketId,
                    });

                    api.axios.post('/api/broadcast/auth', {
                        socket_id: socketId,
                        channel_name: channel.name,
                    }).then((response) => {
                        const authPayload = typeof response.data === 'string'
                            ? JSON.parse(response.data)
                            : response.data;

                        console.log('[PUSHER FINANCEIRO] Canal autorizado pelo backend', {
                            channel: channel.name,
                            has_auth: Boolean(authPayload?.auth),
                        });

                        callback(false, authPayload);
                    }).catch((error) => {
                        console.error('[PUSHER FINANCEIRO] Erro ao autorizar canal privado', {
                            channel: channel.name,
                            status: error?.response?.status,
                            data: error?.response?.data,
                        });
                        callback(true, error);
                    });
                },
            }),
        });

        financePusher.connection.bind('connected', () => {
            console.log('[PUSHER FINANCEIRO] Conectado ao Pusher');
            commit('setFinanceRealtimeConnected', true);
        });

        financePusher.connection.bind('disconnected', () => {
            console.warn('[PUSHER FINANCEIRO] Desconectado do Pusher');
            commit('setFinanceRealtimeConnected', false);
        });

        financePusher.connection.bind('error', (error) => {
            console.error('[PUSHER FINANCEIRO] Erro na conexao Pusher', error);
            commit('setFinanceRealtimeConnected', false);
        });

        financeChannel = financePusher.subscribe(channelName);
        commit('setFinanceRealtimeChannelName', channelName);

        financeChannel.bind('pusher:subscription_succeeded', () => {
            console.log('[PUSHER FINANCEIRO] Inscricao realizada com sucesso', {
                channel: channelName,
            });
            commit('setFinanceRealtimeConnected', true);
        });

        financeChannel.bind('pusher:subscription_error', (error) => {
            console.error('[PUSHER FINANCEIRO] Erro na inscricao do canal', {
                channel: channelName,
                error,
            });
            commit('setFinanceRealtimeConnected', false);
        });

        financeChannel.bind('account.changed', async payload => {
            if (payload.reason === 'couple' || payload.reason === 'preference') await dispatch('fetchCouple');
            if (payload.reason === 'goals') await dispatch('invalidateCoupleContent');
            commit('setFinanceRealtimeLastEvent', payload);
            if (payload.reason === 'payment') {
                await Promise.all([dispatch('setSummary'), dispatch('setInvoiceHistory'), dispatch('setCurrentPlan')]);
            }
            await dispatch('refreshFinanceAfterRealtime');
        });
        financeChannel.bind('finance.changed', (payload) => {
            console.log('[PUSHER FINANCEIRO] Evento finance.changed recebido', payload);
            commit('setFinanceRealtimeLastEvent', payload);
            dispatch('refreshFinanceAfterRealtime');
        });
    },

    stopFinanceRealtime({ commit }) {
        if (financePusher) {
            financePusher.disconnect();
        }

        financePusher = null;
        financeChannel = null;

        commit('setFinanceRealtimeConnected', false);
        commit('setFinanceRealtimeChannelName', '');
    },

    async refreshFinanceAfterRealtime({ dispatch }) {
        console.log('[PUSHER FINANCEIRO] Recarregando dashboard e lançamentos após evento realtime', {
            dashboard_params: state.lastDashboardParams,
            transaction_params: state.lastTransactionParams,
        });

        await Promise.all([
            dispatch('fetchFinanceDashboard', state.lastDashboardParams),
            dispatch('fetchFinanceTransactions', state.lastTransactionParams),
        ]);
    },

    // Compatibility aliases.
    fetchFinancialData({ dispatch }, params) {
        return dispatch('fetchFinanceTransactions', params);
    },
    fetchExpenseData({ dispatch }, params) {
        return dispatch('fetchFinanceTransactions', params);
    },
    fetchCardsFinancialManagementData({ dispatch }, params) {
        return dispatch('fetchFinanceDashboard', params);
    },
};

const mutations = {
    clearSharedFinance(state) {
        state.transactions = []; state.recurringExpenses = [];
        state.dashboard = { current_month: emptyMoney, previous_month: emptyMoney,
            month_comparison: { difference: emptyMoney, percentage: null }, last_15_days: emptyMoney,
            current_week: emptyMoney, recurring_expenses: { total: emptyMoney, items: [] }, categories: [] };
        state.transactionsPagination = { current_page: 1, last_page: 1, per_page: 10, total: 0 };
        state.lastTransactionParams = {}; state.lastDashboardParams = {}; state.lastRecurringParams = {};
        state.financeError = '';
    },
    setFinanceValuesVisibility(state, { userId, visible }) {
        state.valuesVisibilityUserId = userId;
        state.valuesVisible = Boolean(visible);
    },
    setFinanceDashboard(state, data) {
        state.dashboard = {
            ...state.dashboard,
            ...(data || {}),
        };
    },
    setFinanceTransactions(state, data) {
        state.transactions = data;
    },
    setFinanceTransactionsPagination(state, data) {
        state.transactionsPagination = data;
    },
    setFinanceCategories(state, data) {
        state.categories = data;
    },
    setFinanceRecurringExpenses(state, data) {
        state.recurringExpenses = data;
    },
    setFinanceRecurringExpensesPagination(state, data) {
        state.recurringExpensesPagination = data;
    },
    setFinanceRecurringExpensesSummary(state, data) {
        state.recurringExpensesSummary = {
            monthly_estimate: data?.monthly_estimate || emptyMoney,
            active_count: Number(data?.active_count || 0),
            overdue_count: Number(data?.overdue_count || 0),
            upcoming_count: Number(data?.upcoming_count || 0),
            unestimated_count: Number(data?.unestimated_count || 0),
        };
    },
    setLoadingFinance(state, data) {
        state.loadingFinance = data;
    },
    setFinanceError(state, data) {
        state.financeError = data;
    },
    setFinanceRealtimeConnected(state, data) {
        state.realtimeConnected = data;
    },
    setFinanceRealtimeLastEvent(state, data) {
        state.realtimeLastEvent = data;
    },
    setFinanceRealtimeChannelName(state, data) {
        state.realtimeChannelName = data;
    },
    setLastDashboardParams(state, data) {
        state.lastDashboardParams = { ...(data || {}) };
    },
    setLastTransactionParams(state, data) {
        state.lastTransactionParams = { ...(data || {}) };
    },
    setLastRecurringParams(state, data) {
        state.lastRecurringParams = { ...(data || {}) };
    },
};

export default {
    state,
    getters,
    actions,
    mutations
};
